import { Visibility, VisibilityOff } from "@mui/icons-material";
import {
  Box,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { Form, Formik } from "formik";
import { Fragment, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import * as yup from "yup";
import useAPI from "../../../../api/hooks/useAPI";
import useIsIntegrated from "../../../../api/hooks/useIsIntegrated";
import { integrationKeys, onboardingKeys } from "../../../../api/queryKeys";
import PrimaryButton from "../../../../components/buttons/PrimaryButton";
import SecondaryButton from "../../../../components/buttons/SecondaryButton";
import useExpandedList from "../../../../components/hooks/ExpandedList";
import LoadingIcon from "../../../../components/LoadingIcon";
import { ScreenshotAccordion } from "../../../../components/ScreenshotAccordion";
import { Toast } from "../../../../components/Toast";
import { logos } from "../../environment/icons/iconExport";
import Guideline from "../components/Guideline";
import GuidelineInstruction from "../components/GuidelineInstruction";
import GuidelineStep from "../components/GuidelineStep";
import GuidelineStepByStep from "../components/GuidelineStepByStep";
import IntegrationGuideAlert from "../components/IntegrationGuideAlert";
import addApiKey from "./images/add-api-key.png";
import orgKeyImage from "./images/Org-key.png";

const CarbonBlackIntegration = () => {
  const environmentDomainMap = [
    { envName: "EAP01", hrf: "https://defense-eap01.conferdeploy.net" },
    { envName: "prod01", hrf: "https://dashboard.confer.net" },
    { envName: "prod02", hrf: "https://defense.conferdeploy.net" },
    { envName: "prod05", hrf: "https://defense-prod05.conferdeploy.net" },
    { envName: "prod06", hrf: "https://defense-eu.conferdeploy.net" },
    { envName: "prodNRT", hrf: "https://defense-prodnrt.conferdeploy.net" },
    { envName: "prodSyd", hrf: "https://defense-prodsyd.conferdeploy.net" },
    { envName: "produk", hrf: "https://ew2.carbonblackcloud.vmware.com" },
    {
      envName: "awsgovcloud",
      hrf: "https://gprd1usgw1.carbonblack-us-gov.vmware.com",
    },
  ];

  const { isIntegrated, isIntegratedLoading } = useIsIntegrated("carbonblack");
  const [loading, setLoading] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const [searchParams] = useSearchParams();
  const [guideMode, setGuideMode] = useState(false);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (searchParams.has("viewonly")) {
      setGuideMode(true);
    }
  }, [searchParams]);

  const { addToExpandedList, handleExpandAll, handleCollapseAll } =
    useExpandedList();
  const { registerCarbonBlackIntegration } = useAPI();

  const handleClickShowSecret = () => setShowSecret((prv) => !prv);
  const handleMouseDownSecret = () => setShowSecret((prv) => !prv);

  const formInitialValues = {
    apiKey: "",
    apiId: "",
    orgKey: "",
    hostname: "",
  };
  const formCheckoutSchema = yup.object().shape({
    apiKey: yup.string().required("required"),
    apiId: yup.string().required("required"),
    orgKey: yup.string().required("required"),
    hostname: yup.string().required("required"),
  });
  const handleFormSubmit = (values) => {
    setLoading(true);
    registerCarbonBlackIntegration(
      values.apiKey,
      values.apiId,
      values.orgKey,
      values.hostname
    )
      .then((response) => {
        queryClient.invalidateQueries({
          queryKey: integrationKeys.all,
        });
        // only in EDR integrations, we need to invalidate the onboarding context
        queryClient.invalidateQueries({
          queryKey: onboardingKeys.all,
        });
        Toast.success("Carbon Black integration successful");
      })
      .catch((error) => {
        if (error.response) {
          Toast.error(`Something went wrong: ${error.response.data.detail}`);
        } else {
          Toast.error("An error occurred while processing the request.");
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };
  return (
    <Box>
      <Guideline
        title={
          !isIntegrated
            ? "Once integrated, the Breeze platform will automatically read the configurations from CarbonBlack and prioritize the suggested remediation."
            : ""
        }
        image={logos.CarbonBlackLogo}
        name={"CarbonBlack"}
        content={
          loading || isIntegratedLoading ? (
            <LoadingIcon size={25} />
          ) : isIntegrated && !guideMode ? (
            <Fragment>
              <Typography variant={"h4"}>
                CarbonBlack is already integrated!
              </Typography>
            </Fragment>
          ) : (
            <>
              <Box mb="20px">
                <Grid container spacing={2} direction={"row"}>
                  {guideMode && <IntegrationGuideAlert />}
                  <Grid item>
                    <SecondaryButton onClick={handleExpandAll}>
                      Expand All Screenshots
                    </SecondaryButton>
                  </Grid>
                  <Grid item>
                    <SecondaryButton onClick={handleCollapseAll}>
                      Collapse All Screenshots
                    </SecondaryButton>
                  </Grid>
                </Grid>
              </Box>
              <Formik
                onSubmit={handleFormSubmit}
                initialValues={formInitialValues}
                validationSchema={formCheckoutSchema}
              >
                {({
                  values,
                  errors,
                  touched,
                  handleBlur,
                  handleChange,
                  handleSubmit,
                }) => (
                  <Form
                    onSubmit={handleSubmit}
                    style={{ display: "flex", flexDirection: "column" }}
                  >
                    <GuidelineStepByStep>
                      <GuidelineStep title="Log in to VMware Carbon Black Cloud Console">
                        <GuidelineInstruction text="Visit the VMware Carbon Black Cloud Console and log in with your credentials." />
                      </GuidelineStep>
                      <GuidelineStep title="Create an API Key">
                        <GuidelineInstruction text="To create an API Key, go to Settings > API Access > API Keys tab in the Carbon Black Cloud console." />
                        <GuidelineInstruction text="Select Add API Key from the far right." />
                        <ScreenshotAccordion
                          images={[
                            {
                              file: addApiKey,
                              altext: "API key example",
                            },
                          ]}
                          onExpanded={addToExpandedList}
                        />
                        <GuidelineInstruction text="Give the API Key a unique name like 'Breeze token'" />
                        <GuidelineInstruction text="Select 'View Only - Legacy' Access Level type." />
                      </GuidelineStep>
                      <GuidelineStep title="Copy and Save the API Key">
                        <GuidelineInstruction text="Hit save, and you will be provided with your API Key Credentials: API Secret Key, API ID" />
                        <TextField
                          label="API ID"
                          value={values.apiId}
                          name="apiId"
                          margin="normal"
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={!!touched.apiId && !!errors.apiId}
                          helperText={
                            touched.apiId &&
                            errors.apiId &&
                            errors.apiId.toString()
                          }
                          disabled={guideMode}
                        />
                        <TextField
                          label="API Key"
                          value={values.apiKey}
                          name="apiKey"
                          margin="normal"
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={!!touched.apiKey && !!errors.apiKey}
                          type={showSecret ? "text" : "password"}
                          helperText={
                            touched.apiKey &&
                            errors.apiKey &&
                            errors.apiKey.toString()
                          }
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  aria-label="toggle secret visibility"
                                  onClick={handleClickShowSecret}
                                  onMouseDown={handleMouseDownSecret}
                                >
                                  {showSecret ? (
                                    <Visibility />
                                  ) : (
                                    <VisibilityOff />
                                  )}
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                          disabled={guideMode}
                        />
                      </GuidelineStep>
                      <GuidelineStep title="Add org key.">
                        <GuidelineInstruction text="You can find your org_key in the Carbon Black Cloud Console" />
                        <GuidelineInstruction text="Go to Settings > General." />
                        <ScreenshotAccordion
                          images={[
                            {
                              file: orgKeyImage,
                              altext: "Org key example",
                            },
                          ]}
                          onExpanded={addToExpandedList}
                        />
                        <TextField
                          label="Organization Key"
                          style={{ width: "100%" }}
                          name="orgKey"
                          value={values.orgKey}
                          margin="normal"
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={!!touched.orgKey && !!errors.orgKey}
                          helperText={
                            touched.orgKey &&
                            errors.orgKey &&
                            errors.orgKey.toString()
                          }
                          disabled={guideMode}
                        />
                      </GuidelineStep>
                      <GuidelineStep title="Add environment.">
                        <GuidelineInstruction text="The environment domain is part in the web address of your Carbon Black Cloud console." />
                        <GuidelineInstruction text="For example, if this URL is https://defense-prod05.conferdeploy.net," />
                        <GuidelineInstruction text="then the environment is prod05"></GuidelineInstruction>
                        <InputLabel id="environment-label">hostname</InputLabel>
                        <Select
                          labelId="environment-label"
                          id="env-label-id"
                          label="environment"
                          value={values.hostname}
                          name="hostname"
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={!!touched.hostname && !!errors.hostname}
                        >
                          {environmentDomainMap.map((env) => {
                            return (
                              <MenuItem value={env.hrf}>{env.envName}</MenuItem>
                            );
                          })}
                        </Select>
                      </GuidelineStep>
                    </GuidelineStepByStep>

                    <GuidelineStep title="All done, now hit “Connect“ to integrate!">
                      <Box display="flex" justifyContent="center">
                        <PrimaryButton type="submit" disabled={guideMode}>
                          Connect
                        </PrimaryButton>
                      </Box>
                    </GuidelineStep>
                  </Form>
                )}
              </Formik>
            </>
          )
        }
        subTitle={undefined}
        subContent={undefined}
      />
    </Box>
  );
};
export default CarbonBlackIntegration;
